// src/components/modules/TAI/TAIEstadisticas.js

import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Typography,
  DatePicker,
  Space,
  Spin,
  message,
  Table,
  Tag,
  Divider,
} from "antd";
import {
  CarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  TeamOutlined,
  FileTextOutlined,
  DollarOutlined,
  RiseOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import dayjs from "../../../utils/dayjs";
import api from "../../../axiosConfig";
import { formatCurrencyCOP } from "../../../utils/formatCurrency";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// Colores para gráficos
const COLORS = {
  ejecutado: "#52c41a",
  cancelado: "#ff4d4f",
  pendiente: "#faad14",
  asignado: "#1890ff",
  confirmado: "#722ed1",
  interveredal: "#722ed1",
  intermunicipal: "#2f54eb",
  urbano: "#13c2c2",
};

const TAIEstadisticas = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [fechas, setFechas] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);

  const fetchEstadisticas = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fechas && fechas.length === 2) {
        params.append("fechaInicio", fechas[0].format("YYYY-MM-DD"));
        params.append("fechaFin", fechas[1].format("YYYY-MM-DD"));
      }

      const response = await api.get(`/tai/estadisticas-avanzadas?${params.toString()}`);

      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (error) {
      console.error("Error al cargar estadísticas:", error);
      message.error("Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  }, [fechas]);

  useEffect(() => {
    fetchEstadisticas();
  }, [fetchEstadisticas]);

  const handleFechasChange = (dates) => {
    setFechas(dates);
  };

  if (loading || !data) {
    return (
      <div style={{ textAlign: "center", padding: 50 }}>
        <Spin size="large" />
      </div>
    );
  }

  const { indicadores, planillas, top_transportadoras, top_eps, distribucion_trayecto } = data;

  // Columnas para tabla de transportadoras
  const columnasTransportadoras = [
    {
      title: "Transportadora",
      dataIndex: "nombre",
      key: "nombre",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Servicios",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (val) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: "% Total",
      dataIndex: "porcentaje",
      key: "porcentaje",
      align: "right",
      render: (val) => `${val || 0}%`,
    },
  ];

  // Columnas para tabla de EPS
  const columnasEps = [
    {
      title: "EPS",
      dataIndex: "empresa",
      key: "empresa",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Servicios",
      dataIndex: "total",
      key: "total",
      align: "right",
      render: (val) => <Tag color="purple">{val}</Tag>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Filtros de fecha */}
      <Card style={{ marginBottom: 24 }}>
        <Space>
          <Text strong>Período:</Text>
          <RangePicker
            value={fechas}
            onChange={handleFechasChange}
            format="DD/MM/YYYY"
          />
        </Space>
      </Card>

      {/* Indicadores principales */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Traslados TAI"
              value={indicadores.total_traslados}
              prefix={<CarOutlined style={{ color: "#722ed1" }} />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Ejecutados"
              value={indicadores.ejecutados}
              prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ({indicadores.tasa_ejecucion}%)
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Cancelados"
              value={indicadores.cancelados}
              prefix={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
              valueStyle={{ color: "#ff4d4f" }}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ({indicadores.tasa_cancelacion}%)
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Con Transportadora"
              value={indicadores.con_transportadora}
              prefix={<TeamOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff" }}
              suffix={
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ({indicadores.tasa_asignacion}%)
                </Text>
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Estadísticas de Planillas */}
      <Card title={<><FileTextOutlined /> Estadísticas de Planillas</>} style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Statistic
              title="Total Planillas"
              value={planillas.total_planillas}
              prefix={<FileTextOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Statistic
              title="Planillas Facturadas"
              value={planillas.planillas_facturadas}
              valueStyle={{ color: "#52c41a" }}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Statistic
              title="Planillas Pendientes"
              value={planillas.planillas_pendientes}
              valueStyle={{ color: "#faad14" }}
              prefix={<RiseOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Statistic
              title="Valor Total"
              value={planillas.valor_total || 0}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrencyCOP(value)}
            />
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Statistic
              title="Valor Facturado"
              value={planillas.valor_facturado || 0}
              valueStyle={{ color: "#52c41a" }}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrencyCOP(value)}
            />
          </Col>
        </Row>
      </Card>

      {/* Distribución por tipo de trayecto */}
      <Card title="Distribución por Tipo de Trayecto" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          {distribucion_trayecto.map((item) => (
            <Col xs={24} sm={8} key={item.tai_trayecto_tipo}>
              <Card size="small" style={{ textAlign: "center" }}>
                <Statistic
                  title={item.tai_trayecto_tipo?.charAt(0).toUpperCase() + item.tai_trayecto_tipo?.slice(1)}
                  value={item.total}
                  valueStyle={{ color: COLORS[item.tai_trayecto_tipo] || "#8c8c8c" }}
                />
                <Progress
                  percent={Math.round((item.total / indicadores.total_traslados) * 100)}
                  strokeColor={COLORS[item.tai_trayecto_tipo] || "#8c8c8c"}
                  showInfo={true}
                />
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Top Transportadoras y EPS */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card
            title={
              <Space>
                <TrophyOutlined style={{ color: "#faad14" }} />
                Top 5 Transportadoras
              </Space>
            }
          >
            <Table
              dataSource={top_transportadoras}
              columns={columnasTransportadoras}
              pagination={false}
              size="small"
              rowKey="nombre"
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card
            title={
              <Space>
                <TrophyOutlined style={{ color: "#722ed1" }} />
                Top 5 EPS
              </Space>
            }
          >
            <Table
              dataSource={top_eps}
              columns={columnasEps}
              pagination={false}
              size="small"
              rowKey="empresa"
            />
          </Card>
        </Col>
      </Row>

      {/* Indicadores de eficiencia */}
      <Divider />
      <Card title="Indicadores de Eficiencia" style={{ marginTop: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <div style={{ textAlign: "center" }}>
                <Text type="secondary">Tasa de Ejecución</Text>
                <Progress
                  type="circle"
                  percent={indicadores.tasa_ejecucion}
                  strokeColor="#52c41a"
                  width={100}
                />
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <div style={{ textAlign: "center" }}>
                <Text type="secondary">Tasa de Asignación</Text>
                <Progress
                  type="circle"
                  percent={indicadores.tasa_asignacion}
                  strokeColor="#1890ff"
                  width={100}
                />
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <div style={{ textAlign: "center" }}>
                <Text type="secondary">Tasa de Cancelación</Text>
                <Progress
                  type="circle"
                  percent={indicadores.tasa_cancelacion}
                  strokeColor="#ff4d4f"
                  width={100}
                  status={indicadores.tasa_cancelacion > 10 ? "exception" : "normal"}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default TAIEstadisticas;
